import { Tooltip } from "@mui/material";
import React, { ReactElement, useLayoutEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useMatch } from "react-router-dom";
import { RootState } from "../../../store/store";
import GetHeroIcon from "../../../utils/getHeroIcon";
import BackButton from "./BackButton";
import useBreadcrumbs from "./breadCrumbHook";
import "./breadcrumb.css";
import {
  getAuthoringLibraryLabel,
  localizeAuthoringLibraryBreadcrumb
} from "../../../utils/authoringLibraryResources/localizeAuthoringLibraryLabels";
import { useAuthoringLibraryLanguage } from "../../../utils/authoringLibraryResources/useAuthoringLibraryLanguage";

const DynamicBreadcrumb = (props: any): JSX.Element | null => {
  const { page } = props;
  const { loading, current } = useSelector(
    (state: RootState) => state?.breadCrumb
  );
  const isHitachiOrg = useSelector(
    (state: RootState) => state?.UserInfoSlice?.profile?.isHitachiOrg
  );
  const isContent = useMatch("/content-viewer/:content-type/:contentId");
  const isContentPlayer = useMatch("/content-player/:content-type/:contentId");
  const isSessionPage = location.pathname.startsWith("/event-author/session");
  const isAgendaPage = location.pathname.startsWith("/event-author/agenda");
  const isLearningpath = location.pathname.startsWith(
    "/content-viewer/learning-path"
  );

  function applyEnvColors() {
    const breadcrumb = document.querySelector(".breadcrumb");
    if (breadcrumb) {
      (breadcrumb as HTMLElement).style.setProperty(
        "--breadcrumb-active-color",
        "#D40000"
      );
    }
  }

  useLayoutEffect(() => {
    if (isHitachiOrg) {
      applyEnvColors();
    }
  }, []);

  if (loading && page !== "home" && page !== "cafeParent" && !current[page])
    return <div></div>;

  const renderCrumb = (page: string): JSX.Element | null => {
    return current[page] ? (
      <Tooltip title={current[page]?.title}>
        <span>{current[page]?.title}</span>
      </Tooltip>
    ) : null;
  };

  const renderPodcastViewerCrumb = (): JSX.Element | null => {
    if (location.pathname.startsWith("/content-viewer/podcast-episode")) {
      return current["podcast-episode"] ? (
        <Tooltip title={current["podcast-episode"]?.parent?.title}>
          <span>{current["podcast-episode"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else if (
      location.pathname.startsWith("/content-player/podcast-episode")
    ) {
      return current["podcast-episode-play"] ? (
        <Tooltip title={current["podcast-episode-play"]?.parent?.title}>
          <span>{current["podcast-episode-play"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    }
    return current["podcast"] ? (
      <Tooltip title={current["podcast"]?.title}>
        <span>{current["podcast"]?.title}</span>
      </Tooltip>
    ) : null;
  };

  const renderPodcastEpisodeViewerCrumb = (): JSX.Element | null => {
    if (location.pathname.startsWith("/content-viewer/podcast-episode")) {
      return current["podcast-episode"] ? (
        <Tooltip title={current["podcast-episode"]?.title}>
          <span>{current["podcast-episode"]?.title}</span>
        </Tooltip>
      ) : null;
    }
    return current["podcast"]?.parent?.title ? (
      <Tooltip title={current["podcast"]?.parent?.title}>
        <span>{current["podcast"]?.parent?.title}</span>
      </Tooltip>
    ) : null;
  };

  const renderLPCrumb = (): JSX.Element | null => {
    if (
      (isContent ||
        isContentPlayer ||
        location.pathname.startsWith("/quizinitiator")) &&
      !isLearningpath
    ) {
      return (
        <Tooltip title={"Learningpath"}>
          <span>Learning Path</span>
        </Tooltip>
      );
    } else if (location.pathname.endsWith("/admin-dashboard")) {
      if (loading) {
        return (
          <Tooltip title="Learning Path">
            <span>Learning Path"</span>
          </Tooltip>
        );
      }
      return current["learningpath-dashboard"] ? (
        <Tooltip title={current["learningpath-dashboard"]?.parent?.title}>
          <span>{current["learningpath-dashboard"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    }

    return current["learning-path"] ? (
      <Tooltip title={current["learning-path"]?.title}>
        <span>{current["learning-path"]?.title}</span>
      </Tooltip>
    ) : null;
  };

  const renderEventAuthoringCrumb = (): JSX.Element | null => {
    if (isSessionPage) {
      /* if we are in the session page
      and we are handling the event(parent)
      breadcrumb section we get the name of the event */
      return current["session-authoring"] ? (
        <Tooltip title={current["session-authoring"]?.parent?.title}>
          <span>{current["session-authoring"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else {
      return current[page] ? (
        <Tooltip title={current[page]?.title}>
          <span>{current[page]?.title}</span>
        </Tooltip>
      ) : (
        <span> Event </span>
      );
    }
  };

  const renderSessionAuthoringCrumb = (): JSX.Element | null => {
    if (isAgendaPage) {
      /* if we are in the agenda page
      and we are handling the session(parent) breadcrumb 
      section we get the name of the event */
      return current["agenda-editing"] ? (
        <Tooltip title={current["agenda-editing"]?.parent?.title}>
          <span>{current["agenda-editing"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else {
      return current[page] ? (
        <Tooltip title={current[page]?.title}>
          <span>{current[page]?.title}</span>
        </Tooltip>
      ) : null;
    }
  };

  const renderEventReviewCrumb = (): JSX.Element | null => {
    const isSessionReviewPage = location.pathname.startsWith(
      "/event-admin/session"
    );
    const isAgendaReviewPage = location.pathname.startsWith(
      "/event-admin/agenda"
    );

    if (isSessionReviewPage) {
      /* if we are in the session page 
       and we are handling the event(parent) 
      breadcrumb section we get the name of the event */
      return current["sessionReview"] ? (
        <Tooltip title={current["sessionReview"]?.parent?.title}>
          <span>{current["sessionReview"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else if (isAgendaReviewPage) {
      /* if we are in the agenda page
      // and we are handling the session(parent)
      // breadcrumb section we get the name of the section */
      return current["agendaReview"] ? (
        <Tooltip title={current["agendaReview"]?.parent?.parent?.title}>
          <span>{current["agendaReview"]?.parent?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else {
      return current[page] ? (
        <Tooltip title={current[page]?.title}>
          <span>{current[page]?.title}</span>
        </Tooltip>
      ) : null;
    }
  };

  const renderSessionReviewCrumb = (): JSX.Element | null => {
    const isAgendaReviewPage = location.pathname.startsWith(
      "/event-admin/agenda"
    );
    if (isAgendaReviewPage) {
      return current["agendaReview"] ? (
        <Tooltip title={current["agendaReview"]?.parent?.title}>
          <span>{current["agendaReview"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else {
      return current[page] ? (
        <Tooltip title={current[page]?.title}>
          <span>{current[page]?.title}</span>
        </Tooltip>
      ) : null;
    }
  };

  const renderEventDetailsCrumb = (): JSX.Element | null => {
    if (location.pathname.includes("/survey/")) {
      return current["take-survey"] ? (
        <Tooltip title={current["take-survey"]?.parent?.title}>
          <span>{current["take-survey"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else if (location.pathname.startsWith("/event/manage-sessions/")) {
      return current["manage-sessions"] ? (
        <Tooltip title={current["manage-sessions"]?.parent?.title}>
          <span>{current["manage-sessions"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else if (location.pathname.startsWith("/event/manage-session-agenda/")) {
      return current["manage-session-agenda"] ? (
        <Tooltip title={current["manage-session-agenda"]?.parent?.title}>
          <span>{current["manage-session-agenda"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    } else {
      return current["event-details"] ? (
        <Tooltip title={current["event-details"]?.title}>
          <span>{current["event-details"]?.title}</span>
        </Tooltip>
      ) : null;
    }
  };

  const renderCourseCrumb = () => {
    if (location.pathname.endsWith("/admin-dashboard")) {
      if (loading) {
        return (
          <Tooltip title="Course">
            <span>Course"</span>
          </Tooltip>
        );
      }
      return current["course-dashboard"] ? (
        <Tooltip title={current["course-dashboard"]?.parent?.title}>
          <span>{current["course-dashboard"]?.parent?.title}</span>
        </Tooltip>
      ) : null;
    }

    return renderCrumb("course");
  };

  const renderPodcastPlayerCrumb = () => {
    return current["podcast-episode-play"] ? (
      <Tooltip title={current["podcast-episode-play"]?.title}>
        <span>{current["podcast-episode-play"]?.title}</span>
      </Tooltip>
    ) : null;
  };

  const renderCafePlayerCrumb = () => {
    return current["cafePlayer"] ? (
      <Tooltip title={current["cafePlayer"]?.title}>
        <span>{current["cafePlayer"]?.title}</span>
      </Tooltip>
    ) : null;
  };

  const renderCafeParentCrumb = () => {
    const refValue = new URLSearchParams(location.search).get("ref");
    return <span>{refValue === "/search" ? "Explore" : "Cafe Lounge"}</span>;
  };

  switch (page) {
    case "course":
      return renderCourseCrumb();
    case "learning-path":
      return renderLPCrumb();
    case "event-authoring":
      return renderEventAuthoringCrumb();
    case "session-authoring":
      return renderSessionAuthoringCrumb();
    case "eventReview":
      return renderEventReviewCrumb();
    case "sessionReview":
      return renderSessionReviewCrumb();
    case "event-details":
      return renderEventDetailsCrumb();
    case "podcast":
      return renderPodcastViewerCrumb();
    case "podcast-episode":
      return renderPodcastEpisodeViewerCrumb();
    case "podcast-episode-play":
      return renderPodcastPlayerCrumb();
    case "cafePlayer":
      return renderCafePlayerCrumb();
    case "cafeParent":
      return renderCafeParentCrumb();
    case "home":
      return (
        <GetHeroIcon
          name={"HomeIcon"}
          className="h-5 w-6 lg:w-5 lg:ml-2 hover:color-[#00875d]"
          variant="outline"
          testId="homeCrumbIcon"
        />
      );
    default:
      return renderCrumb(page);
  }
};

const getRoutes = (language: string) => {
  return [
    {
      path: "/quizinitiator",
      breadcrumb: null,
      children: [
        {
          path: ":id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "content" }
        }
      ]
    },
    {
      path: "/content-viewer/survey/:surveyId/:parentId",
      breadcrumb: "Take Survey"
    },
    {
      path: "/content-viewer/survey/:surveyId",
      breadcrumb: null
    },
    {
      path: "/content-viewer/survey/",
      breadcrumb: null
    },
    {
      path: "/survey/survey-result/:surveyId/:parentId",
      breadcrumb: "Survey Result"
    },
    {
      path: "/survey",
      breadcrumb: null
    },
    {
      path: "/survey/survey-result",
      breadcrumb: null
    },
    {
      path: "/survey/survey-result/:surveyId",
      breadcrumb: null
    },
    {
      path: "/survey/survey-result/platform-feedback",
      breadcrumb: null
    },
    {
      path: "/survey/survey-result/platform-feedback/:surveyId",
      breadcrumb: "Platform Survey"
    },
    {
      path: "/content-viewer/survey/platform-feedback",
      breadcrumb: null
    },
    {
      path: "/content-viewer/survey/platform-feedback/:surveyId",
      breadcrumb: "Take Platform Survey"
    },
    {
      path: "/",
      breadcrumb: DynamicBreadcrumb,
      props: { page: "home" }
    },
    {
      path: "/search",
      breadcrumb: "Explore",
      key: "explore"
    },
    {
      path: "/calendar",
      breadcrumb: null,
      children: [
        {
          path: "calendar-dashboard-admin",
          breadcrumb: "Event Library Calendar",
          key: "admin-calendar"
        },
        {
          path: "calendar-dashboard-learner",
          key: "learner-calendar",
          breadcrumb: "My Calendar"
        },
        {
          path: "calendar-dashboard-global",
          breadcrumb: "Training Calendar",
          key: "training-calendar"
        }
      ]
    },

    {
      path: "/content-player",
      breadcrumb: null,
      children: [
        {
          path: ":contentType",
          breadcrumb: null
        },
        {
          path: "podcast-episode/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "podcast-episode-play" }
        },
        {
          path: ":contentType/:contentId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "content" }
        },
        {
          path: "survey/:quizId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "contentPlayer" }
        }
      ]
    },

    {
      path: "/content-player",
      breadcrumb: null,
      children: [
        {
          path: "survey",
          breadcrumb: null
        },
        {
          path: "survey/:surveyId",
          breadcrumb: null
        },
        {
          path: "survey/:surveyId/:parentId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "survey" }
        }
      ]
    },

    {
      path: "/content-viewer",
      breadcrumb: null,
      children: [
        {
          path: "course",
          breadcrumb: null
        },
        {
          path: "course/:courseId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "content" },
          children: [
            {
              path: "admin-dashboard",
              breadcrumb: "Admin Dashboard"
            }
          ]
        },
        {
          path: "course-internal/:courseId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "course" },
          children: [
            {
              path: "admin-dashboard",
              breadcrumb: "Admin Dashboard"
            }
          ]
        },
        {
          path: "assessment/:scheduleId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "public-assessment" },
          children: [
            {
              path: "assessment",
              breadcrumb: "Assessment"
            }
          ]
        },
        {
          path: "podcast/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "podcast" }
        },

        {
          path: "podcast-episode/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "podcast-episode" }
        },
        {
          path: ":content-type",
          breadcrumb: null
        },
        {
          path: "learning-path/:learningpath",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "learning-path" },
          children: [
            {
              path: "admin-dashboard",
              breadcrumb: "Admin Dashboard"
            }
          ]
        },
        {
          path: ":content-type/:contentId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "content" }
        },

        {
          path: "learning-path",
          breadcrumb: null
        },
        {
          path: "assessment",
          breadcrumb: null
        },
        {
          path: "assessment/:scheduleId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "public-assessment" }
        }
      ]
    },

    {
      path: "/event",
      breadcrumb: null,
      children: [
        {
          path: "event-details/:eventId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "event-details" },
          children: [
            {
              path: "survey",
              breadcrumb: null,
              children: [
                {
                  path: ":surveyId",
                  breadcrumb: null,
                  children: [
                    {
                      path: ":parentId",
                      breadcrumb: DynamicBreadcrumb,
                      props: { page: "take-survey" }
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          path: "event-details/",
          breadcrumb: null
        },
        {
          path: "manage-sessions/",
          breadcrumb: null
        },
        {
          path: "manage-sessions/:sessionId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "manage-sessions" }
        },
        {
          path: "manage-session-agenda/",
          breadcrumb: null
        },
        {
          path: "manage-session-agenda/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "manage-session-agenda" }
        },
        {
          path: "manage-participants",
          breadcrumb: null
        },
        {
          path: "manage-participants/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "manage-participants" }
        },
        {
          path: "manage-configurations",
          breadcrumb: null
        },
        {
          path: "manage-configurations/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "manage-configurations" }
        },
        {
          path: "update-event-information",
          breadcrumb: null
        },
        {
          path: "update-event-information/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "update-event-information" }
        }
      ]
    },

    {
      path: "/event-author",
      breadcrumb: null,
      children: [
        {
          path: "agenda/:agendaId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "agenda-editing" }
        },
        {
          path: "session/:sessionId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "session-authoring" },
          ignoreWithChildren: true,
          children: [
            {
              path: ":tab",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "session-authoring" }
            }
          ]
        },
        {
          path: "event/:eventId",
          breadcrumb: DynamicBreadcrumb,
          key: "event-authoring",
          props: { page: "event-authoring" },
          ignoreWithChildren: true,
          children: [
            {
              path: ":tab",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "event-authoring" }
            }
          ]
        },
        {
          path: "event/:eventId/template",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "event-template" }
        },
        {
          path: "create-event",
          breadcrumb: "Create Event",
          props: { page: "event-authoring" }
        },
        {
          path: "session",
          breadcrumb: null
        },
        {
          path: "event/",
          breadcrumb: null
        },
        {
          path: "agenda/",
          breadcrumb: null
        }
      ]
    },
    {
      path: "/event-admin/",
      breadcrumb: null,
      children: [
        {
          path: "event-review/",
          breadcrumb: null
        },
        {
          path: "event-review/:eventId",
          breadcrumb: DynamicBreadcrumb,
          key: "event-review",
          props: { page: "eventReview" }
        },
        {
          path: "event-author/event/:eventId/template",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "event-template" }
        },
        {
          path: "session/:sessionId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "sessionReview" }
        },
        {
          path: "session",
          breadcrumb: null
        },
        {
          path: "agenda/:agendaId",
          breadcrumb: "Agenda"
        },
        {
          path: "agenda",
          breadcrumb: null
        }
      ]
    },
    {
      path: "/profile",
      key: "profile",
      breadcrumb: "Profile",
      children: [
        {
          path: "manager-dashboard",
          breadcrumb: "Manager Dashboard",
          key: "manager-dashboard",
          children: [
            {
              path: ":selectedUser",
              breadcrumb: null
            },
            {
              path: ":selectedUser/assigned-contents",
              key: "assigned-contents",
              // breadcrumb: DynamicBreadcrumb,
              // props: { page: "assigned-contents" }
              breadcrumb: "Assigned Contents"
            },
            {
              path: "reportee/:userId",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "profile-page" }
            }
          ]
        },
        {
          path: ":userId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "profile-page" }
        },
        {
          path: "activity-dashboard/",
          breadcrumb: null,
          children: [
            {
              path: "in-progress",
              key: "in-progress",
              breadcrumb: "In Progress Content"
            },
            {
              path: "assigned-content",
              key: "assigned-content",
              breadcrumb: "Assigned Content"
            },
            {
              path: "completed-content",
              key: "completed-content",
              breadcrumb: "Completed Content"
            },
            {
              path: "overview-dashboard",
              breadcrumb: "Activity Dashboard"
            },
            {
              path: "completed-content",
              key: "completed-content",
              breadcrumb: "Completed Content"
            },
            {
              path: "in-progress-assessment",
              key: "in-progress-assessment",
              breadcrumb: "In Progress Assessment"
            },
            {
              path: "assigned-assessment",
              key: "assigned-assessment",
              breadcrumb: "Assigned Assessment"
            },
            {
              path: "completed-assessment",
              key: "completed-assessment",
              breadcrumb: "Completed Assessment"
            }
          ]
        },
        {
          path: "assessment-result",
          breadcrumb: null
        },
        {
          path: "assessment-result/:id",
          key: "assessment",
          breadcrumb: "Assessment Result"
        }
      ]
    },
    {
      path: "/tos",
      breadcrumb: "Terms of Service"
    },
    {
      path: "/authoring/",
      breadcrumb: null,
      children: [
        {
          path: "content-authoring/:contentType/:contentId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "contentAuthoring" }
        },
        {
          path: "content-authoring/:contentType/",
          breadcrumb: null
        },
        {
          path: "content-authoring",
          key: "content-authoring",
          breadcrumb: "Learning Artifacts Library",
          children: [
            {
              path: "quiz",
              key: "quiz-authoring",
              breadcrumb: "Quiz Authoring",
              children: [
                {
                  path: ":id",
                  breadcrumb: null
                }
              ]
            },
            {
              path: "quiz_assesment",
              key: "assesment-authoring",
              breadcrumb: "Assessment Authoring",
              children: [
                {
                  path: ":id",
                  breadcrumb: null
                }
              ]
            }
          ]
        },
        {
          path: "content-authoring/:contentType/edit/:contentId",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "contentAuthoring" }
        },
        {
          path: "content-authoring/:contentType/edit/",
          breadcrumb: null
        },
        {
          path: "event-authoring",
          key: "event-library",
          breadcrumb: "Event Library"
        },
        {
          path: "learningpath-authoring",
          key: "learningpath-authoring",
          breadcrumb: "Learning Path Library"
        },
        {
          path: "learning-path/",
          breadcrumb: null
        },
        {
          path: "learning-path/:id",
          breadcrumb: "Edit Learning Path"
        },
        {
          path: "learning-path/edit",
          breadcrumb: null
        },
        {
          path: "learning-path/edit/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "learningPathEditing" }
        },
        {
          path: "course-authoring",
          key: "course-authoring",
          breadcrumb: "Course Library"
        },
        {
          path: "course-authoring",
          breadcrumb: null
        },
        {
          path: "course-authoring/:id",
          breadcrumb: "Edit Course"
        },
        {
          path: "course-authoring/edit/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "contentAuthoring" }
        },
        {
          path: "course-authoring/edit",
          breadcrumb: null
        },
        {
          path: "group-authoring",
          key: "group-authoring",
          breadcrumb: "Group Library",
          ignoreWithChildren: true
        },
        {
          path: "group-authoring",
          breadcrumb: null
        },
        {
          path: "group-authoring/:id",
          breadcrumb: "Group Authoring"
        },
        {
          path: "group-authoring/manage-users/:id",
          breadcrumb: "Manage Users"
        },
        {
          path: "question-paper-authoring",
          key: "question-paper-library",
          breadcrumb: getAuthoringLibraryLabel(
            "questionPaperLibrary",
            language
          ),
          children: [
            {
              path: "question-authoring",
              breadcrumb: getAuthoringLibraryLabel(
                "questionAuthoring",
                language
              ),
              key: "question-authoring",
              children: [
                {
                  path: "question-review",
                  breadcrumb: getAuthoringLibraryLabel(
                    "reviewQuestions",
                    language
                  )
                },
                {
                  path: "bulk-upload-history",
                  breadcrumb: getAuthoringLibraryLabel(
                    "bulkUploadHistory",
                    language
                  )
                },
                {
                  path: "questions-bulk-action-history",
                  breadcrumb: getAuthoringLibraryLabel(
                    "bulkActionHistory",
                    language
                  )
                }
              ]
            },
            {
              path: "review-qp",
              breadcrumb: null
            },
            {
              path: "review-qp/:id",
              breadcrumb: null
            },
            {
              path: "preview-qp",
              breadcrumb: null
            },
            {
              path: "preview-qp/:id",
              breadcrumb: null
            },
            {
              path: "question-authoring/:subtype",
              breadcrumb: null
            },
            {
              path: "question-authoring/:subtype/:id",
              breadcrumb: null
            },
            {
              path: "question-authoring/:subtype/:id/:version",
              breadcrumb: getAuthoringLibraryLabel("editQuestion", language),
              key: "question-create",
              ignoreWithChildren: true
            },
            {
              path: "question-authoring/:subtype/:id/question-preview",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "question-preview" }
            },

            {
              path: "review-qp/:id/:version",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "question-paper-preview" }
            },
            {
              path: "preview-qp/:id/:version",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "question-paper-preview" }
            },
            {
              path: "create-qp",
              breadcrumb: null
            },
            {
              path: "create-qp/:id",
              breadcrumb: null
            },
            {
              path: "create-qp/:id/:version",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "question-paper-create" }
            }
          ]
        },
        {
          path: "assessment-authoring",
          key: "assessment-library",
          breadcrumb: getAuthoringLibraryLabel("assessmentLibrary", language),
          children: [
            // {
            //   path: "question-authoring",
            //   breadcrumb: "Question Authoring",
            //   key: "question-authoring",
            //   children: [
            //     {
            //       path: "question-review",
            //       breadcrumb: "Review Questions",
            //     },
            //   ],
            // },
            {
              path: "preview-schedule",
              breadcrumb: null
            },
            {
              path: "preview-schedule/:id",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "preview-schedule" }
            },
            {
              path: "invite-participants",
              breadcrumb: null
            },
            {
              path: "invite-participants/:id",
              breadcrumb: getAuthoringLibraryLabel(
                "inviteParticipants",
                language
              )
            },
            {
              path: "external-user-bulk-upload-history",
              breadcrumb: null
            },
            {
              path: "external-user-bulk-upload-history/:scheduleId",
              breadcrumb: getAuthoringLibraryLabel(
                "externalUserBulkUploadHistory",
                language
              )
            },
            {
              path: "assessment-schedule",
              breadcrumb: null
            },
            {
              path: "assessment-schedule/:id",
              breadcrumb: getAuthoringLibraryLabel(
                "assessmentSchedule",
                language
              )
            },
            {
              path: "assessment-report",
              breadcrumb: null
            },
            {
              path: "assessment-report/:scheduleId",
              breadcrumb: getAuthoringLibraryLabel("assessmentReport", language)
            },
            {
              path: "assessment-report/:scheduleId/:userId",
              breadcrumb: null
            },
            {
              path: "assessment-report/:scheduleId/:userId/:ongoingId",
              breadcrumb: DynamicBreadcrumb,
              props: { page: "candidate-report" }
            }
          ]
        }
      ]
    },
    {
      path: "/super-admin/",
      breadcrumb: "Super Admin",
      key: "super-admin",
      ignoreWithChildren: true,
      children: [
        {
          path: "user-group-authoring",
          breadcrumb: "Group Library"
        },
        {
          path: "user-group-authoring/:id",
          breadcrumb: "Create New Group",
          ignoreWithChildren: true
        },
        {
          path: ":id/manage-users",
          breadcrumb: "Manage Users"
        },
        {
          path: "user-group-authoring/:id/manage-users",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "manage-users" }
        }
      ]
    },
    {
      path: "/learning-admin/",
      breadcrumb: "Learning Admin",
      key: "learning-admin",
      ignoreWithChildren: true,
      children: [
        // {
        //   path: "user-group-authoring",
        //   breadcrumb: "Group Library",
        // },
        // {
        //   path: "user-group-authoring/:id",
        //   breadcrumb: "Create New Group",
        //   ignoreWithChildren: true,
        // },
        // {
        //   path: ":id/manage-users",
        //   breadcrumb: "Manage Users",
        // },
        {
          path: ":name/assigned-contents",
          breadcrumb: "Assigned Contents"
        },
        {
          path: ":id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "content-progress" }
        }
      ]
    },
    {
      path: "/cafe-lounge/",
      breadcrumb: DynamicBreadcrumb,
      props: { page: "cafeParent" },
      key: "cafe-lounge",
      children: [
        {
          path: "create-new-cafe",
          breadcrumb: null,
          ignoreWithChildren: true
        },
        {
          path: "create-new-cafe/:id",
          breadcrumb: "Cafe Authoring"
        },
        {
          path: "view/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "cafePlayer" }
        },
        {
          path: ":id",
          breadcrumb: null
        }
      ]
    },
    {
      path: "/error",
      breadcrumb: null,
      children: [
        {
          path: "page-not-found",
          breadcrumb: "Page Not Found"
        },
        {
          path: "attempt-exhaust",
          breadcrumb: null
        },
        {
          path: "attempt-exhaust/:id",
          breadcrumb: "Attempts Exhausted"
        },
        {
          path: "error-survey/:id",
          breadcrumb: DynamicBreadcrumb,
          props: { page: "survey-name" }
        }
      ]
    },
    {
      path: "/ai-interview",
      breadcrumb: "GLX Client Interview Mirror",
      children: [
        {
          path: "interview/",
          breadcrumb: null
        },
        {
          path: "interview/:id",
          breadcrumb: "Session"
        }
      ]
    }
  ];
};

const generateBreadcrumb = (
  match: any,
  breadcrumb: any,
  i: number,
  lastElement: boolean
): ReactElement => {
  if (i !== 0) {
    const renderLink = () => {
      if (lastElement) {
        return <span className="text-body">{breadcrumb}</span>;
      }

      const isCafeParentCrumb = match?.route?.props?.page === "cafeParent";
      const refValue = new URLSearchParams(location.search).get("ref");
      const linkTo =
        isCafeParentCrumb && refValue === "/search"
          ? "/search"
          : match?.referrer
            ? `${match.pathname}?ref=${match.referrer}`
            : match?.pathname;

      return <Link to={linkTo}>{breadcrumb}</Link>;
    };
    return (
      <li className="flex items-center flex-1 w-10 truncate max-w-fit">
        <GetHeroIcon
          name={"ChevronRightIcon"}
          variant="outline"
          className="pt-0.5 md:w-4.5 lg:h-4.5 w-5 h-4.5"
        />
        <div className="text-sm text-primary truncate w-fit breadcrumb-item md:text-base hover:text-primary-light">
          {renderLink()}
        </div>
      </li>
    );
  }
  return (
    <li className="breadcrumb-item text-body px-0 py-[0.1rem] flex">
      <Link to={match.pathname}>{breadcrumb}</Link>
    </li>
  );
};

const BreadCrumb: React.FC = () => {
  const { previous, current, parent } = useSelector(
    (state: RootState) => state.breadCrumb
  );
  const profileLanguage = useSelector(
    (state: RootState) => state.UserInfoSlice.profile?.preferences?.language
  );
  const { language } = useAuthoringLibraryLanguage(profileLanguage);
  const routes = useMemo(() => getRoutes(language), [language]);

  const breadcrumbs = useBreadcrumbs(routes, { previous, current, parent });
  return (
    <div className="flex items-center justify-start w-full px-3 m-5 mx-0 sm:px-10">
      <BackButton excludedPaths={["/onboarding", "/tos"]} />
      <ul className="flex justify-start flex-1 w-full p-0 ml-2 list-none breadcrumb">
        {breadcrumbs.map(({ match, breadcrumb }, i, arr) => {
          const lastElement = arr.length - 1 === i;
          const localizedBreadcrumb = localizeAuthoringLibraryBreadcrumb(
            breadcrumb,
            language
          );
          return generateBreadcrumb(match, localizedBreadcrumb, i, lastElement);
        })}
      </ul>
    </div>
  );
};

export default BreadCrumb;
